import React from 'react'
import styled from '@emotion/styled'
import { font } from '@mobi/component-library/Foundations'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import { RadioGroupItem } from '@mobi/component-library/Selection&Input/CheckboxAndRadioGroups'
import { RadioGroup } from '@mobi/component-library/Areas/Blackbook/BlackbookAlertPrompt.styled'
import { useAuthenticatedAccount } from '@core/Components/RequireAuth/hooks'
import { isPushNotificationAvailable } from '@core/Utils/notifications/pushNotificationAlerts'
import { incrementQuickCounter } from '@core/Services/Analytics/helpers/quickCounter'
import { useBlackbookDetailsQuery } from '@core/Areas/Blackbook/Hooks/useBlackbookDetailsQuery'

type BlackbookSettingsProps = {
  isTakeABreakActive: boolean
}

export const BlackbookSettings: React.FC<BlackbookSettingsProps> = ({ isTakeABreakActive }) => {
  const { accountNumber } = useAuthenticatedAccount()
  const {
    detailsQuery,
    pushAlertQuery,
    mutatePushAlertAsync,
    mutateSmsEmailAlertAsync,
    isPushAlertMutating,
    isSmsEmailAlertMutating,
    isLoading,
  } = useBlackbookDetailsQuery(accountNumber)

  const disableSettings =
    isLoading || isPushAlertMutating || isSmsEmailAlertMutating || isTakeABreakActive

  const onAlertMethodUpdate = (method: 'SendPush' | 'SendEmail' | 'SendSms') => async () => {
    try {
      if (method === 'SendPush') {
        const newPushAlertStatus = !(pushAlertQuery.data ?? false)
        await mutatePushAlertAsync(newPushAlertStatus)
        incrementQuickCounter(`blackbook-manual-push-update-to-${newPushAlertStatus}`)
      } else if (method === 'SendEmail') {
        await mutateSmsEmailAlertAsync({
          SendEmail: !(detailsQuery.data?.SendEmail ?? false),
          SendSms: detailsQuery.data?.SendSms ?? false,
        })
      } else if (method === 'SendSms') {
        await mutateSmsEmailAlertAsync({
          SendEmail: detailsQuery.data?.SendEmail ?? false,
          SendSms: !(detailsQuery.data?.SendSms ?? false),
        })
      }
    } catch (error) {
      addToast({
        title:
          (error as { message?: string })?.message ||
          'Failed to update alert method. Please try again.',
        id: 'blackbook-settings-update-error',
        icon: 'error',
        timeoutMs: 1_500,
      })
    }
  }

  return (
    <BlackbookSettingsStyled data-testid='blackbook-settings'>
      <div className='selection-methods'>
        <div className='selection-methods-title'>How would you like to receive notifications?</div>
        <RadioGroup>
          {isPushNotificationAvailable && (
            <RadioGroupItem
              id='push-notification-method'
              size='sm'
              iconName='LineNotificationBox'
              isChecked={pushAlertQuery.data || false}
              isDisabled={disableSettings}
              onClick={onAlertMethodUpdate('SendPush')}
              type='Tick'
            >
              Push Notifications
            </RadioGroupItem>
          )}
          <RadioGroupItem
            id='email-notification-method'
            size='sm'
            iconName='LineMail02'
            isChecked={detailsQuery.data?.SendEmail || false}
            isDisabled={disableSettings}
            onClick={onAlertMethodUpdate('SendEmail')}
            type='Tick'
          >
            Email
          </RadioGroupItem>
          <RadioGroupItem
            id='sms-notification-method'
            size='sm'
            iconName='LineMessageTextSquare02'
            isChecked={detailsQuery.data?.SendSms || false}
            isDisabled={disableSettings}
            onClick={onAlertMethodUpdate('SendSms')}
            type='Tick'
          >
            SMS
          </RadioGroupItem>
        </RadioGroup>
      </div>
    </BlackbookSettingsStyled>
  )
}

// ======
// Styles
// ======

const BlackbookSettingsStyled = styled.div({
  fontFamily: font.family.primary,
  display: 'grid',
  gridTemplateRows: 'min-content min-content min-content 1fr',
  gap: '1rem',

  'div.selection-methods': {
    display: 'flex',
    flexDirection: 'column',
  },

  'div.selection-methods-title': {
    fontWeight: font.weight.bold,
    paddingBottom: '1.5rem',
    ...font.size.xl,
  },
})
